import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PrefetchLink from '@/components/PrefetchLink';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, MapPin, Calendar, Users, Filter, Globe, CalendarDays, Repeat, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ALL_COUNTRIES as COUNTRIES } from '@/constants/countries';
import SearchableSelect from '@/components/SearchableSelect';
import MembershipCountdownChip from '@/components/MembershipCountdownChip';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';

// Helper function to get date ranges
const getDateRange = (filter) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  switch (filter) {
    case 'past':
      // Past-only view — signal to the fetch layer to flip to past_only=true
      // via a null start/end. Consumers should key off filter === 'past'.
      return { start: null, end: null };
    case 'this-week': {
      const endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + (7 - today.getDay())); // End of this week (Sunday)
      return {
        start: today.toISOString().split('T')[0],
        end: endOfWeek.toISOString().split('T')[0]
      };
    }
    case 'this-month': {
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return {
        start: today.toISOString().split('T')[0],
        end: endOfMonth.toISOString().split('T')[0]
      };
    }
    case 'next-month': {
      const startOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return {
        start: startOfNextMonth.toISOString().split('T')[0],
        end: endOfNextMonth.toISOString().split('T')[0]
      };
    }
    default: // 'all'
      return { start: today.toISOString().split('T')[0], end: null };
  }
};

// Get display label for date filter
const getDateFilterLabel = (filter) => {
  const today = new Date();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  
  switch (filter) {
    case 'past':
      return 'Past';
    case 'this-week':
      return 'This Week';
    case 'this-month':
      return monthNames[today.getMonth()];
    case 'next-month':
      return monthNames[(today.getMonth() + 1) % 12];
    default:
      return 'All Upcoming';
  }
};

const EventDiscovery = () => {
  const { user } = useAuth();
  const isIOSApp = useIsIOSApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [countryEvents, setCountryEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Infinite-scroll state for the main `/events` feed. The split-by-location
  // and radius endpoints already return a focused result so pagination is
  // only wired for the fallback /events path.
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useRef(0);
  const loadMoreRef = useRef(null);
  const PAGE_SIZE = 20;
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [dismissedEvents, setDismissedEvents] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [recommended, setRecommended] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  
  // Get date filter from URL or default to 'this-month'
  const [dateFilter, setDateFilter] = useState(() => {
    const urlDateFilter = searchParams.get('when');
    return urlDateFilter || 'this-month';
  });
  
  const scrollPositionRef = React.useRef(0);
  const [filters, setFilters] = useState({
    country: '',
    city: '',
    category: '',
  });
  
  // Refs
  const headerRef = useRef(null);

  // Distance filter: '' (city only) | '25' | '50' | '100' (km radius around city)
  const [distanceKm, setDistanceKm] = useState(() => searchParams.get('distance') || '');

  // Cache list state + scroll position across route navigation so back from
  // an event detail page lands the user instantly on the same list at the
  // same scroll Y — no spinner, no refetch, no scroll jump.
  const listPersistence = useListStatePersistence({
    key: 'eventDiscovery',
    state: { events, countryEvents, search, filters, dateFilter, distanceKm },
    enabled: !loading && (events.length > 0 || countryEvents.length > 0),
    onRestore: (cached) => {
      if (Array.isArray(cached.events)) setEvents(cached.events);
      if (Array.isArray(cached.countryEvents)) setCountryEvents(cached.countryEvents);
      if (typeof cached.search === 'string') setSearch(cached.search);
      if (cached.filters) setFilters(cached.filters);
      if (cached.dateFilter) setDateFilter(cached.dateFilter);
      if (typeof cached.distanceKm === 'string') setDistanceKm(cached.distanceKm);
      // Mark loading complete so the cached list renders immediately.
      setLoading(false);
      // After events are in the DOM, jump instantly to the page header — no
      // animation, no flash, no fight with the browser's auto-restore.
      requestAnimationFrame(() => {
        headerRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    },
  });
  
  // Helper to update both state and URL atomically
  const updateDistance = (newValue) => {
    setDistanceKm(newValue);
    const newParams = new URLSearchParams(searchParams);
    if (newValue) newParams.set('distance', newValue);
    else newParams.delete('distance');
    setSearchParams(newParams, { replace: true });
  };
  
  // Reset distance when city is cleared (radius makes no sense without a center)
  useEffect(() => {
    if (!filters.city && distanceKm) {
      updateDistance('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.city]);

  // Update URL when date filter changes
  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    // Update URL parameter
    const newParams = new URLSearchParams(searchParams);
    if (newFilter === 'this-month') {
      newParams.delete('when'); // Remove param for default value
    } else {
      newParams.set('when', newFilter);
    }
    setSearchParams(newParams, { replace: true });
  };

  // Scroll to top when page loads (e.g., when navigating back)
  // Also disable browser's automatic scroll restoration
  useEffect(() => {
    // Disable browser's scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    // Scroll to top immediately
    window.scrollTo(0, 0);
    
    // Also scroll after a small delay to handle any async content loading
    const scrollTimer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    
    return () => clearTimeout(scrollTimer);
  }, []);

  // Save scroll position before filter change and restore after events load
  const handleFilterChange = (newFilters) => {
    scrollPositionRef.current = window.scrollY;
    setFilters(newFilters);
  };

  // Restore scroll position after events are loaded
  useEffect(() => {
    if (!loading && scrollPositionRef.current > 0) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPositionRef.current);
      });
    }
  }, [loading, events]);

  // Initialize filters based on user's browsing community on first load
  useEffect(() => {
    const initializeLocationFilter = async () => {
      // Only run if not already initialized AND user is available
      if (filtersInitialized || !user) return;
      
      try {
        // Fetch the latest browsing community from API for accurate data
        const response = await axios.get(`${API}/users/me/browsing-community`);
        const { browsing_city, browsing_country } = response.data;
        
        if (browsing_city || browsing_country) {
          setFilters(prev => ({
            ...prev,
            city: browsing_city || '',
            country: browsing_country || '',
          }));
        }
      } catch (error) {
        // Fallback to user object data if API fails
        const userCity = user.browsing_city || user.residence_city || '';
        const userCountry = user.browsing_country || user.residence_country || '';
        
        if (userCity || userCountry) {
          setFilters(prev => ({
            ...prev,
            city: userCity,
            country: userCountry,
          }));
        }
      }
      // Only mark as initialized after we've actually processed the user data
      setFiltersInitialized(true);
    };

    initializeLocationFilter();
  }, [user, filtersInitialized]);

  useEffect(() => {
    fetchCategories();
    fetchDismissedEvents();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [filters, dismissedEvents, dateFilter, distanceKm]);

  const fetchDismissedEvents = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/events/dismissed`);
      setDismissedEvents(response.data.dismissed_events || []);
    } catch (error) {
      console.error('Failed to fetch dismissed events');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/event-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Past events view: fetch strictly-past events, newest first.
      const isPast = dateFilter === 'past';

      if (filters.country) params.append('country', filters.country);
      // For the PAST view we deliberately drop the `city` filter. Past events
      // are historical browsing — people travel, attend events in other
      // cities in their country, or simply want to see what happened around
      // them without being pinned to one city (e.g. a Budapest-based user
      // looking at a July flight event out of Tokol). Keeping `country`
      // preserves a sensible scope; dropping `city` prevents past events
      // from vanishing the moment the city filter loads in.
      if (!isPast && filters.city) params.append('city', filters.city);
      if (filters.category) params.append('category', filters.category);
      if (search) params.append('search', search);

      if (isPast) {
        params.append('past_only', 'true');
      }

      // Add date range filter (skip for past view — server handles it)
      const dateRange = getDateRange(dateFilter);
      if (!isPast) {
        if (dateRange.start) params.append('start_date', dateRange.start);
        if (dateRange.end) params.append('end_date', dateRange.end);
      }

      // When the user has set BOTH city + country, use the split-by-location endpoint
      // so we can show "events in city" + "other events in country".
      // Past view always goes through the generic /events endpoint because
      // by-location and by-radius don't support past_only.
      if (!isPast && distanceKm && filters.city) {
        // Radius mode: single sorted list of events within N km of the city
        const rParams = new URLSearchParams();
        rParams.append('city', filters.city);
        if (filters.country) rParams.append('country', filters.country);
        rParams.append('radius_km', distanceKm);
        if (filters.category) rParams.append('category', filters.category);
        if (search) rParams.append('search', search);
        if (isPast) rParams.append('past_only', 'true');
        if (!isPast && dateRange.start) rParams.append('start_date', dateRange.start);
        if (!isPast && dateRange.end) rParams.append('end_date', dateRange.end);
        const response = await axios.get(`${API}/events/by-radius?${rParams.toString()}`);
        const radiusEvents = (response.data.events || []).filter(
          event => !dismissedEvents.includes(event.event_id)
        );
        setEvents(radiusEvents);
        setCountryEvents([]);
        setHasMore(false);
      } else if (!isPast && filters.city && filters.country) {
        const response = await axios.get(`${API}/events/by-location?${params.toString()}`);
        const cityEvents = (response.data.city_events || []).filter(
          event => !dismissedEvents.includes(event.event_id)
        );
        const otherCountryEvents = (response.data.country_events || []).filter(
          event => !dismissedEvents.includes(event.event_id)
        );
        setEvents(cityEvents);
        setCountryEvents(otherCountryEvents);
        setHasMore(false);
      } else {
        // Paginated path: first page uses PAGE_SIZE; "Load more" bumps offset.
        params.append('limit', String(PAGE_SIZE));
        params.append('offset', '0');
        const response = await axios.get(`${API}/events?${params.toString()}`);
        // Dismissed events are hidden from UPCOMING browsing (the user said
        // "not interested"), but when the user is intentionally browsing
        // PAST events they're looking at history — surface the dismissed
        // ones too so this view isn't accidentally empty.
        const filteredEvents = isPast
          ? response.data
          : response.data.filter(event => !dismissedEvents.includes(event.event_id));
        setEvents(filteredEvents);
        setCountryEvents([]);
        offsetRef.current = PAGE_SIZE;
        setHasMore(String(response.headers?.['x-has-more'] || '').toLowerCase() === 'true');
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Infinite scroll: fetch the next page of 20 for the generic /events feed
  // (not applicable when the by-location or by-radius path is used — those
  // already return bounded result sets).
  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const isPast = dateFilter === 'past';
      const params = new URLSearchParams();
      if (filters.country) params.append('country', filters.country);
      if (!isPast && filters.city) params.append('city', filters.city);
      if (filters.category) params.append('category', filters.category);
      if (search) params.append('search', search);
      if (isPast) params.append('past_only', 'true');
      const dateRange = getDateRange(dateFilter);
      if (!isPast) {
        if (dateRange.start) params.append('start_date', dateRange.start);
        if (dateRange.end) params.append('end_date', dateRange.end);
      }
      params.append('limit', String(PAGE_SIZE));
      params.append('offset', String(offsetRef.current));
      const response = await axios.get(`${API}/events?${params.toString()}`);
      const page = (isPast ? response.data : response.data.filter(e => !dismissedEvents.includes(e.event_id)));
      setEvents((prev) => {
        // Guard against duplicates if the user filtered + scrolled quickly
        const seen = new Set(prev.map(e => e.event_id));
        return [...prev, ...page.filter(e => !seen.has(e.event_id))];
      });
      offsetRef.current += PAGE_SIZE;
      setHasMore(String(response.headers?.['x-has-more'] || '').toLowerCase() === 'true');
    } catch (error) {
      console.error('Failed to load more:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Keep the current date filter - don't change it automatically
    fetchEvents();
  };

  // Infinite-scroll: when the sentinel enters the viewport, auto-load more.
  // Keeps the manual "Load more" button as a fallback for touch devices that
  // don't fire IntersectionObserver as reliably.
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: '400px 0px' }
    );
    io.observe(node);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, dateFilter, filters.city, filters.country, filters.category, search]);

  // Fetch personalized recommendations for logged-in users
  useEffect(() => {
    if (!user) return;
    const fetchRecommended = async () => {
      setLoadingRecs(true);
      try {
        const { data } = await axios.get(`${API}/events/recommended`);
        setRecommended(data);
      } catch {
        // Silently fail — recommendations are non-critical
      } finally {
        setLoadingRecs(false);
      }
    };
    fetchRecommended();
  }, [user]);

  // Group recurring events (same title + same creator) into a single card.
  // Primary = the occurrence closest to today (next upcoming if any exist,
  // otherwise the most recent past). This ensures the card's image, date,
  // and metadata reflect the CURRENT state of the series rather than the
  // very first occurrence — the earliest date often has stale placeholder
  // imagery while newer dates carry the correct hero image.
  const buildGroupedEvents = (eventList) => {
    const groups = {};
    eventList.forEach((event) => {
      const key = `${event.title.trim().toLowerCase()}__${event.creator_id}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(event);
    });
    const todayIso = new Date().toISOString().split('T')[0];
    Object.values(groups).forEach((group) => {
      group.sort((a, b) => (a.event_date > b.event_date ? 1 : -1));
    });
    return Object.entries(groups).map(([key, group]) => {
      // Find first occurrence with event_date >= today; if none, take the
      // last (most recent past) occurrence.
      const upcomingIdx = group.findIndex((e) => (e.event_date || '') >= todayIso);
      const primary = upcomingIdx >= 0 ? group[upcomingIdx] : group[group.length - 1];
      return {
        key,
        primary,
        allDates: group,
        isRecurring: group.length > 1,
      };
    });
  };
  
  const groupedEvents = React.useMemo(() => buildGroupedEvents(events), [events]);
  const groupedCountryEvents = React.useMemo(() => buildGroupedEvents(countryEvents), [countryEvents]);
  
  // Reusable event card renderer (used for both city + country sections)
  const renderEventCardGroup = ({ key, primary: event, allDates, isRecurring }, index, testIdPrefix = 'event-card') => (
    <motion.div
      key={key}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <PrefetchLink to={`/events/${event.event_id}`} eventId={event.event_id}>
        <div
          className="event-card bg-white rounded-3xl border-2 border-border overflow-hidden hover:shadow-xl transition-all duration-300 group"
          data-testid={`${testIdPrefix}-${index}`}
        >
          <div className="overflow-hidden h-48 relative">
            <img
              src={event.image_url || 'https://images.unsplash.com/photo-1719508784075-a49e1dcc0c0e?crop=entropy&cs=srgb&fm=jpg&q=85'}
              alt={event.title}
              className="event-card-image w-full h-full object-cover"
              onError={(e) => {
                // Broken/expired image_url would otherwise render as alt-text
                // over a broken-image icon. Fall back to the same default we
                // use when image_url is absent so the card layout stays clean.
                if (e.currentTarget.src !== 'https://images.unsplash.com/photo-1719508784075-a49e1dcc0c0e?crop=entropy&cs=srgb&fm=jpg&q=85') {
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1719508784075-a49e1dcc0c0e?crop=entropy&cs=srgb&fm=jpg&q=85';
                }
              }}
            />
            {isRecurring && (
              <span className="absolute top-3 right-3 px-2.5 py-1 bg-violet-600 text-white text-xs font-bold rounded-full flex items-center gap-1 shadow-lg">
                <Repeat className="w-3 h-3" />
                {allDates.length} dates
              </span>
            )}
            {typeof event.distance_km === 'number' && (
              <span className="absolute top-3 left-3 px-2.5 py-1 bg-white/95 text-gray-700 text-xs font-bold rounded-full flex items-center gap-1 shadow-lg" data-testid={`distance-badge-${index}`}>
                <MapPin className="w-3 h-3" />
                {event.distance_km} km
              </span>
            )}
          </div>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="px-3 py-1 bg-accent text-accent-foreground text-xs font-bold rounded-full">
                {event.group_name || event.category}
              </span>
              {event.price === 0 || event.is_free ? (
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">FREE</span>
              ) : null}
              {event.is_shared_listing && (
                <span
                  className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-full border border-amber-200"
                  data-testid="event-shared-listing-badge"
                  title="Community pick — the person who listed this event isn't the host"
                >
                  ★ Shared
                </span>
              )}
            </div>
            <h3 className="text-xl font-heading font-bold mb-2 line-clamp-2">{event.title}</h3>
            <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{event.description}</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center text-muted-foreground">
                <MapPin className="w-4 h-4 mr-2" />
                {event.city}, {event.country}
              </div>
              <div className="flex items-center text-muted-foreground">
                <Calendar className="w-4 h-4 mr-2" />
                {event.event_date} at {event.event_time}
              </div>
              <div className="flex items-center text-muted-foreground">
                <Users className="w-4 h-4 mr-2" />
                {event.seats_taken ?? (event.attendees_count || event.attendees?.length || 0)} attending
                {event.max_attendees && ` / ${event.max_attendees}`}
              </div>
              {(() => {
                if (!event.max_attendees) return null;
                const taken = event.seats_taken ?? (event.attendees_count || event.attendees?.length || 0);
                const remaining = event.max_attendees - taken;
                if (remaining <= 0) {
                  return (
                    <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 text-[11px] font-bold px-2.5 py-1 w-fit" data-testid={`${testIdPrefix}-seats-left-${event.event_id}`}>
                      Full — waitlist open
                    </span>
                  );
                }
                if (remaining === 1) {
                  return (
                    <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 text-[11px] font-bold px-2.5 py-1 w-fit animate-pulse" data-testid={`${testIdPrefix}-seats-left-${event.event_id}`}>
                      🔥 1 seat left!
                    </span>
                  );
                }
                if (remaining / event.max_attendees <= 0.2) {
                  return (
                    <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold px-2.5 py-1 w-fit" data-testid={`${testIdPrefix}-seats-left-${event.event_id}`}>
                      Only {remaining} seats left
                    </span>
                  );
                }
                return null;
              })()}
              {event.nationalities_count > 0 && (
                <div className="flex items-center text-muted-foreground">
                  <Globe className="w-4 h-4 mr-2" />
                  {event.nationalities_count} nationalit{event.nationalities_count === 1 ? 'y' : 'ies'}
                </div>
              )}
            </div>
            <Button className="w-full mt-4 rounded-full bg-primary hover:bg-primary/90" size="sm">
              View Event
            </Button>
          </div>
        </div>
      </PrefetchLink>
      {isRecurring && (
        <div className="mt-2">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); toggleGroupExpand(key); }}
            className="w-full flex items-center justify-center gap-1.5 text-sm text-violet-600 hover:text-violet-800 font-medium py-2 rounded-xl bg-violet-50 hover:bg-violet-100 transition-colors"
          >
            {expandedGroups[key] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expandedGroups[key] ? 'Hide dates' : `View all ${allDates.length} dates`}
          </button>
          {expandedGroups[key] && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-2 space-y-1.5"
            >
              {allDates.map((d) => (
                <Link
                  key={d.event_id}
                  to={`/events/${d.event_id}`}
                  className="flex items-center justify-between px-4 py-2.5 bg-white border border-border rounded-xl text-sm hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="font-medium">{d.event_date}</span>
                    <span className="text-muted-foreground">at {d.event_time}</span>
                  </div>
                  <span className="text-muted-foreground text-xs">
                    {d.attendees_count || d.attendees?.length || 0} going
                  </span>
                </Link>
              ))}
            </motion.div>
          )}
        </div>
      )}
    </motion.div>
  );

  const toggleGroupExpand = (key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12" ref={headerRef}>
            <div className="mb-3">
              <MembershipCountdownChip />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold font-heading tracking-tighter leading-none mb-4">
              Discover Events
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Find meetups, gatherings, and events near you
            </p>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-3xl p-6 md:p-8 border-2 border-border mb-12 brutal-shadow">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                    <Input
                      type="text"
                      placeholder="Search events..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="h-12 pl-12 rounded-xl border-2"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-bold"
                  data-testid="search-button"
                >
                  Search
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  type="text"
                  placeholder="City"
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="h-12 rounded-xl border-2"
                  data-testid="filter-city"
                />
                <SearchableSelect
                  options={[{ label: 'All Countries', value: '' }, ...COUNTRIES.map(c => ({ label: c, value: c }))]}
                  value={filters.country || ''}
                  onValueChange={(value) => handleFilterChange({ ...filters, country: value })}
                  placeholder="Country"
                  searchPlaceholder="Type to search..."
                  data-testid="filter-country"
                />
                <Select
                  value={filters.category || 'all'}
                  onValueChange={(value) => handleFilterChange({ ...filters, category: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="h-12 rounded-xl border-2" data-testid="filter-category">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={distanceKm || 'city-only'}
                  onValueChange={(value) => {
                    updateDistance(value === 'city-only' ? '' : value);
                  }}
                  disabled={!filters.city}
                >
                  <SelectTrigger className="h-12 rounded-xl border-2" data-testid="filter-distance">
                    <SelectValue placeholder="Distance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="city-only">City only</SelectItem>
                    <SelectItem value="25">Within 25 km</SelectItem>
                    <SelectItem value="50">Within 50 km</SelectItem>
                    <SelectItem value="100">Within 100 km</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date Filter Tabs */}
              <div className="flex flex-wrap gap-2 pt-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mr-2">
                  <CalendarDays className="w-4 h-4" />
                  <span>When:</span>
                </div>
                {['this-week', 'this-month', 'next-month', 'all', 'past'].map((filter) => (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => handleDateFilterChange(filter)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      dateFilter === filter
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`date-filter-${filter}`}
                  >
                    {getDateFilterLabel(filter)}
                  </button>
                ))}
              </div>
            </form>
          </div>

          {/* Recommended for you */}
          {user && recommended.length > 0 && (
            <div className="mb-10" data-testid="recommended-section">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                </div>
                <h2 className="text-lg font-heading font-bold">Recommended for you</h2>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-thin">
                {recommended.map((event, i) => (
                  <Link
                    key={event.event_id}
                    to={`/events/${event.event_id}`}
                    className="flex-shrink-0 w-72 bg-white rounded-2xl border-2 border-amber-100 overflow-hidden hover:shadow-lg hover:border-amber-200 transition-all group"
                    data-testid={`recommended-card-${i}`}
                  >
                    <div className="h-32 overflow-hidden relative">
                      <img
                        src={event.image_url || 'https://images.unsplash.com/photo-1719508784075-a49e1dcc0c0e?crop=entropy&cs=srgb&fm=jpg&q=85'}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          if (e.currentTarget.src !== 'https://images.unsplash.com/photo-1719508784075-a49e1dcc0c0e?crop=entropy&cs=srgb&fm=jpg&q=85') {
                            e.currentTarget.src = 'https://images.unsplash.com/photo-1719508784075-a49e1dcc0c0e?crop=entropy&cs=srgb&fm=jpg&q=85';
                          }
                        }}
                      />
                      <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
                        {(event.recommendation_reasons || []).map((r) => (
                          <span key={r} className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-500 text-white shadow-sm">
                            {r === 'in_your_city' ? 'In your city' : r === 'matches_interests' ? 'Matches interests' : r === 'expats_like_you' ? 'Expats like you' : r === 'popular' ? 'Popular' : r}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="px-2 py-0.5 bg-accent text-accent-foreground text-[10px] font-bold rounded-full">{event.group_name || event.category}</span>
                        {event.is_shared_listing && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-bold rounded-full border border-amber-200" title="Community pick">★ Shared</span>
                        )}
                      </div>
                      <h3 className="text-sm font-bold mt-2 line-clamp-1">{event.title}</h3>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1.5">
                        <Calendar className="w-3 h-3" />
                        {event.event_date} at {event.event_time}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <MapPin className="w-3 h-3" />
                        {event.city}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Events Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <div className="text-lg text-muted-foreground">Loading events...</div>
            </div>
          ) : groupedEvents.length === 0 && groupedCountryEvents.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-6"
            >
              <div className="max-w-md mx-auto bg-white rounded-2xl p-6 border-2 border-border shadow-lg">
                <h3 className="text-xl font-heading font-bold mb-2">
                  {filters.city ? `No events in ${filters.city} yet` : 'No events found'}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {filters.city ? (
                    <>
                      There are no events scheduled in <span className="font-semibold text-primary">{filters.city}</span> at the moment. 
                      Be the first to create one!
                    </>
                  ) : filters.country ? (
                    <>
                      No events found in <span className="font-semibold text-primary">{filters.country}</span>. 
                      Why not be the first to organize one?
                    </>
                  ) : filters.category ? (
                    <>
                      No <span className="font-semibold text-primary">{filters.category}</span> events available right now.
                    </>
                  ) : (
                    <>
                      Be the first to create an event and connect with expats!
                    </>
                  )}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <Link to={isIOSApp ? "/create-event" : "/membership"}>
                    <Button className="rounded-full bg-primary px-6" size="sm">
                      Create an Event
                    </Button>
                  </Link>
                  {(filters.city || filters.country || filters.category) && (
                    <Button 
                      variant="outline" 
                      onClick={() => setFilters({ city: '', country: '', category: '' })} 
                      className="rounded-full px-6"
                      size="sm"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {groupedEvents.map((group, index) => renderEventCardGroup(group, index, 'event-card'))}
              </div>

              {/* Infinite-scroll sentinel + button fallback for the generic /events feed */}
              {hasMore && (
                <div
                  ref={loadMoreRef}
                  className="flex items-center justify-center py-8"
                  data-testid="events-load-more"
                >
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-6 py-3 rounded-full border-2 border-primary text-primary font-semibold hover:bg-primary/5 disabled:opacity-60"
                    data-testid="events-load-more-btn"
                  >
                    {loadingMore ? 'Loading…' : 'Load more events'}
                  </button>
                </div>
              )}
              
              {/* Other events in the same country — shown only when user filtered by city */}
              {filters.city && filters.country && groupedCountryEvents.length > 0 && (
                <div className="mt-12">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl md:text-3xl font-heading font-bold">
                        Other events in {filters.country}
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        Discover events elsewhere in your country.
                      </p>
                    </div>
                    <span className="px-3 py-1 bg-accent text-accent-foreground text-xs font-bold rounded-full">
                      {groupedCountryEvents.length} event{groupedCountryEvents.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="country-events-grid">
                    {groupedCountryEvents.map((group, index) => renderEventCardGroup(group, index, 'country-event-card'))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EventDiscovery;